import { ThemeContext } from '@tracsis/library/src/context/ThemeContext';
import cx from 'classnames';
import { Link } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useContext } from 'react';

import { Image } from '../../index';
import { ArrowRight, Download, NewWindow } from '../Icon/index';
import * as styles from './Card.module.css';

const CardBase = ({
	children,
	thumbnail,
	type = 'arrow',
	alt,
	date,
	direction,
	background = 'white',
	theme = 'classic',
	shadow = false,
	width,
	buttonText,
	eyebrow,
}: {
	children: React.ReactNode;
	thumbnail?: IGatsbyImageData | string | null;
	type?: 'arrow' | 'download' | 'external' | 'blank';
	alt?: string;
	date?: string;
	direction?: 'row';
	background?: 'white' | 'grey' | 'lightBlue';
	theme?: 'classic' | 'plain' | 'share';
	shadow?: boolean;
	width?: 'quarter';
	buttonText?: string;
	eyebrow?: string;
}) => {
	const { variant } = useContext(ThemeContext);

	return (
		<div
			className={cx(
				styles.card,
				styles[`${background}Background`],
				styles[variant],
				styles[theme],
				styles[width],
				{
					[styles.shadow]: shadow,
					[styles[direction]]: direction,
				}
			)}>
			{thumbnail && alt && (
				<div className={styles.bound}>
					<div className={styles.image}>
						<Image image={thumbnail} alt={alt} />
					</div>
				</div>
			)}
			<div className={styles.content}>
				{eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
				{children}

				<div className={styles.metadata}>
					{date && <p className={styles.date}>{date}</p>}
					{type === 'arrow' && !buttonText && (
						<div className={cx(styles.arrow, styles[variant])}>
							<ArrowRight />
						</div>
					)}
					{type === 'download' && !buttonText && (
						<div className={styles.arrow}>
							<Download />
						</div>
					)}
					{type === 'external' && !buttonText && (
						<div className={styles.arrow}>
							<NewWindow />
						</div>
					)}

					{buttonText && (
						<div className={styles.arrow}>
							<span>{buttonText}</span>

							{type === 'arrow' && <ArrowRight />}
							{type === 'download' && <Download />}
							{type === 'external' && <NewWindow />}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const Card = ({
	children,
	thumbnail,
	alt,
	to,
	type = 'arrow',
	date,
	direction,
	background = 'white',
	theme = 'classic',
	shadow = false,
	feature = false,
	casestudy = false,
	className,
	buttonText,
	width,
	onClick,
	eyebrow,
}: {
	children: React.ReactNode;
	thumbnail?: IGatsbyImageData | string | null;
	alt?: string;
	to?: string;
	type?: 'arrow' | 'download' | 'external' | 'blank';
	date?: string;
	direction?: 'row';
	background?: 'white' | 'grey' | 'lightBlue';
	theme?: 'classic' | 'plain' | 'share';
	shadow?: boolean; // Used for white on white
	feature?: boolean;
	casestudy?: boolean;
	className?: string;
	buttonText?: string;
	width?: 'quarter';
	onClick?: (...args: any) => void;
	eyebrow?: string;
}) => {
	const { variant } = useContext(ThemeContext);

	const internal = /^\/(?!\/)/.test(to);

	// If has download, wrap in link
	if (type === 'download') {
		return (
			<a
				href={to}
				download
				className={cx(styles.link, className, {
					[styles[direction]]: direction,
					[styles.feature]: feature,
				})}>
				<CardBase
					children={children}
					thumbnail={thumbnail}
					type="download"
					alt={alt}
					to={to}
					date={date}
					direction={direction}
					background={background}
					theme={theme}
					shadow={shadow}
					className={className}
					width={width}
					buttonText={buttonText}
					eyebrow={eyebrow}
				/>
			</a>
		);
	}

	// If has link, wrap in link
	if (to && internal)
		return (
			<Link
				to={to}
				className={cx(styles.link, className, {
					[styles[direction]]: direction,
					[styles.feature]: feature,
					[styles.casestudy]: casestudy,
					[styles.casestudy]: casestudy,
				})}
				onClick={onClick}>
				<CardBase
					children={children}
					thumbnail={thumbnail}
					alt={alt}
					to={to}
					date={date}
					direction={direction}
					background={background}
					theme={theme}
					shadow={shadow}
					className={className}
					width={width}
					buttonText={buttonText}
					eyebrow={eyebrow}
				/>
			</Link>
		);

	// If has external link
	if (to && !internal)
		return (
			<a
				href={to}
				target="_blank"
				rel="noopener noreferrer"
				className={cx(styles.link, className, {
					[styles[direction]]: direction,
					[styles.feature]: feature,
				})}
				onClick={onClick}>
				<CardBase
					children={children}
					thumbnail={thumbnail}
					alt={alt}
					to={to}
					date={date}
					direction={direction}
					background={background}
					theme={theme}
					shadow={shadow}
					className={className}
					width={width}
					buttonText={buttonText}
					eyebrow={eyebrow}
					type={type}
				/>
			</a>
		);

	// Otherwise output standard card
	return (
		<CardBase
			children={children}
			thumbnail={thumbnail}
			type="blank"
			alt={alt}
			to={to}
			date={date}
			direction={direction}
			background={background}
			theme={theme}
			className={className}
			width={width}
			buttonText={buttonText}
			eyebrow={eyebrow}
		/>
	);
};

export default Card;
